import type { StoragePort } from './storagePort.ts';
import { WriteQueue } from './writeQueue.ts';

/**
 * Saving and restoring a library of player-authored content.
 *
 * Custom Skills and custom Backgrounds are the same problem twice: one file
 * holding definitions that crawlers reference by ID, which must never be
 * silently overwritten, never accept malformed content, and always keep a
 * backup. Doing that twice would mean two chances to get the write lock wrong.
 *
 * A content type supplies a `ContentLibrarySpec` and inherits all of it.
 */

/**
 * Whether the library on disk could be read.
 *
 *  - `new`        there is no file yet. Normal, and writable.
 *  - `loaded`     a valid library was read.
 *  - `unreadable` a file exists that cannot be migrated or validated. The
 *                 returned library is an empty PLACEHOLDER, not the player's
 *                 content, and must never be written back over the original.
 */
export type LibraryStatus = 'new' | 'loaded' | 'unreadable';

export interface LoadedLibrary<T> {
  library: T;
  problems: string[];
  status: LibraryStatus;
}

export interface ContentLibrarySpec<T> {
  /** File name, without extension. Reaches the file system, so keep it plain. */
  name: string;
  /** What this holds, for messages: "custom Skill", "custom Background". */
  label: string;
  createEmpty(now: string): T;
  migrate(raw: unknown): Record<string, unknown> | null;
  validate(raw: unknown): { valid: boolean; problems: string[] };
}

export class CustomContentRepository<T extends { updatedAt: string }> {
  protected readonly storage: StoragePort;
  protected readonly now: () => string;
  protected readonly queue: WriteQueue;
  private readonly spec: ContentLibrarySpec<T>;

  constructor(
    spec: ContentLibrarySpec<T>,
    storage: StoragePort,
    now: () => string = () => new Date().toISOString(),
    queue: WriteQueue = new WriteQueue(),
  ) {
    this.spec = spec;
    this.storage = storage;
    this.now = now;
    this.queue = queue;
  }

  /** Everything wrong with a stored record, or an empty list if it is fine. */
  private describeProblems(raw: unknown): string[] {
    const migrated = this.spec.migrate(raw);
    if (migrated === null) {
      return [`The ${this.spec.label} library is not in a format this version understands.`];
    }
    const check = this.spec.validate(migrated);
    return check.valid ? [] : check.problems;
  }

  /**
   * Returns the saved library, or a new empty one.
   *
   * A library that fails migration or validation is NOT loaded and NOT
   * overwritten. `status` says which case this is, so the caller can refuse to
   * present a damaged library as an empty editable one.
   */
  async load(): Promise<LoadedLibrary<T>> {
    const raw = await this.storage.readCustomContent(this.spec.name);
    if (raw === null) {
      return { library: this.spec.createEmpty(this.now()), problems: [], status: 'new' };
    }

    const problems = this.describeProblems(raw);
    if (problems.length > 0) {
      return { library: this.spec.createEmpty(this.now()), problems, status: 'unreadable' };
    }
    return { library: this.spec.migrate(raw) as unknown as T, problems: [], status: 'loaded' };
  }

  /**
   * True when a file exists that the application cannot read. While this is
   * true nothing may be written: the file is the only copy of whatever the
   * player had.
   */
  async isLocked(): Promise<boolean> {
    const raw = await this.storage.readCustomContent(this.spec.name);
    return raw !== null && this.describeProblems(raw).length > 0;
  }

  /**
   * Saves the library.
   *
   * Refuses in two situations, and the FIRST is the important one:
   *
   *  1. An unreadable file already exists. Re-checked on every write rather
   *     than remembered from `load`, so no caller can reach the disk by holding
   *     a stale repository, skipping the load, or ignoring what load returned.
   *     The guard cannot be bypassed because it is not advisory.
   *  2. The library being written would not load back. Failing loudly here is
   *     better than writing a file the next start would reject.
   */
  async save(library: T): Promise<T> {
    const existing = await this.storage.readCustomContent(this.spec.name);
    if (existing !== null) {
      const existingProblems = this.describeProblems(existing);
      if (existingProblems.length > 0) {
        throw new Error(
          `The existing ${this.spec.label} library cannot be read, so it has not been changed. ` +
            'Nothing was written. Repair or move the file first:\n' +
            existingProblems.join('\n'),
        );
      }
    }

    const stamped: T = { ...library, updatedAt: this.now() };
    const check = this.spec.validate(stamped);
    if (!check.valid) {
      throw new Error(
        `Refusing to save an invalid ${this.spec.label} library:\n${check.problems.join('\n')}`,
      );
    }
    return this.queue.run(`content:${this.spec.name}`, async () => {
      await this.storage.writeCustomContent(this.spec.name, stamped);
      return stamped;
    });
  }

  drain(): Promise<void> {
    return this.queue.drain();
  }
}
